import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
// @ts-ignore — AuthContext es .jsx
import { useAuth } from "@/contexts/AuthContext";
import { HomeLayout } from "../components/layout/HomeLayout";
import { BeginnerPathPhaseShell } from "../components/beginnerPath/BeginnerPathPhaseShell";
import { RhythmPathLibrary } from "../components/rhythm/path/RhythmPathLibrary";
import { RhythmPathMissionStudio } from "../components/rhythm/path/RhythmPathMissionStudio";
import { useBeginnerPathEntry } from "../hooks/useBeginnerPathEntry";
import {
  getRhythmPathMissionById,
  RHYTHM_PATH_MISSIONS,
} from "../lib/rhythm/rhythmPathPlan";
import {
  loadRhythmPathProgress,
  markRhythmSubMissionComplete,
  saveRhythmPathProgress,
} from "../lib/rhythm/rhythmPathProgress";
import {
  isBeginnerPathPreviewUser,
  loadBeginnerPathProgress,
  markBeginnerPhaseComplete,
  saveBeginnerPathProgress,
} from "../lib/beginnerPath/beginnerPathPlan";
import { HOME_THEME as C } from "../lib/homeTheme";
import { BEGINNER_PATH_PAGE_CLASS } from "../lib/beginnerPath/beginnerPathLayout";

export const RhythmPathPage: React.FC = () => {
  const navigate = useNavigate();
  const { fromPath, phase } = useBeginnerPathEntry();
  const pathMode = fromPath && phase?.id === "rhythm-basics";
  const { user } = useAuth() as { user?: { email?: string | null } | null };
  const preview = useMemo(
    () => isBeginnerPathPreviewUser(user?.email),
    [user?.email]
  );

  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  const [progress, setProgress] = useState(loadRhythmPathProgress);

  useEffect(() => {
    saveRhythmPathProgress(progress);
    const allDone = RHYTHM_PATH_MISSIONS.every((m) =>
      progress.completedMissionIds.includes(m.id)
    );
    if (allDone) {
      const bp = loadBeginnerPathProgress();
      if (!bp.completedPhaseIds.includes("rhythm-basics")) {
        saveBeginnerPathProgress(markBeginnerPhaseComplete(bp, "rhythm-basics"));
      }
    }
  }, [progress]);

  const activeMission = activeMissionId
    ? getRhythmPathMissionById(activeMissionId)
    : null;

  const handleStepComplete = useCallback(
    (subMissionId: string) => {
      if (!activeMission) return;
      setProgress((p) => {
        const next = markRhythmSubMissionComplete(p, activeMission, subMissionId);
        saveRhythmPathProgress(next);
        return next;
      });
    },
    [activeMission]
  );

  const handleMissionFullyComplete = useCallback(() => {
    setActiveMissionId(null);
    const allDone = RHYTHM_PATH_MISSIONS.every((m) =>
      loadRhythmPathProgress().completedMissionIds.includes(m.id)
    );
    if (allDone && pathMode) {
      const bp = loadBeginnerPathProgress();
      saveBeginnerPathProgress(markBeginnerPhaseComplete(bp, "rhythm-basics"));
      navigate("/learn/path");
    }
  }, [navigate, pathMode]);

  const inner = activeMission ? (
    <RhythmPathMissionStudio
      mission={activeMission}
      wasCompletedBefore={progress.completedMissionIds.includes(activeMission.id)}
      completedSubMissionIds={progress.completedSubMissionIds}
      preview={preview}
      pathMode={!!pathMode}
      onBack={() => setActiveMissionId(null)}
      onStepComplete={handleStepComplete}
      onMissionFullyComplete={handleMissionFullyComplete}
    />
  ) : (
    <RhythmPathLibrary
      progress={progress}
      pathMode={!!pathMode}
      preview={preview}
      onSelectMission={setActiveMissionId}
    />
  );

  if (pathMode && phase) {
    return (
      <BeginnerPathPhaseShell phase={phase} fullBleed={!!activeMission}>
        {inner}
      </BeginnerPathPhaseShell>
    );
  }

  return (
    <HomeLayout>
      <div className={BEGINNER_PATH_PAGE_CLASS}>
        <button
          type="button"
          onClick={() => navigate("/learn/path")}
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors hover:text-[#99f6e4]"
          style={{ color: C.muted }}
        >
          <ArrowLeft className="h-4 w-4" />
          Mapa de caminos
        </button>
        <header className="mt-8 mb-8">
          <p className="text-xs tracking-widest uppercase text-[#99f6e4]/70">
            Camino 2 · Mano derecha
          </p>
          <h1 className="mt-3 font-serif text-3xl font-light text-white/90">
            Motor rítmico
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-white/55">
            Alternancia i·m y pulgar, con pasos seleccionables dentro de cada misión.
          </p>
        </header>
        {inner}
      </div>
    </HomeLayout>
  );
};

export default RhythmPathPage;
